import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useResponsables } from "../datos/useResponsables";
import type { AjustesAgenda } from "../dominio/tipos";
import { formatoFechaHora } from "../negocios/formato";

/**
 * 41 (Robert, 23-24/10/2026): Ajustes del calendario de consultorías propio. Encendido sustituye al enlace
 * de reservas (nunca los dos a la vez). Quién tiene horario y cómo va Google, con «Sincronizar ahora».
 */
export const AgendaAjustes = ({
  valor,
  cambiar,
}: {
  valor: AjustesAgenda;
  cambiar: (a: AjustesAgenda) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { nombre } = useResponsables();
  const { data: estado, refetch } = useQuery({
    queryKey: ["nevora", "agenda-estado"],
    queryFn: () => dataProvider.agendaEstado(),
    retry: false,
  });
  const [sincronizando, setSincronizando] = useState(false);
  const t = (k: string, o?: Record<string, unknown>) =>
    translate(`nevora.agenda_propia.${k}`, o);

  const numero = (
    k:
      | "duracionMin"
      | "bloqueMin"
      | "pasoMin"
      | "antelacionHoras"
      | "dias"
      | "recordatorioHoras",
    unidad: string,
  ) => (
    <div className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0">
      <Label htmlFor={`agenda-${k}`}>{t(k)}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={`agenda-${k}`}
          type="number"
          min={0}
          className="w-24 text-right"
          value={valor[k]}
          onChange={(e) => cambiar({ ...valor, [k]: Number(e.target.value) })}
        />
        <span className="text-sm text-muted-foreground w-12">{t(unidad)}</span>
      </div>
    </div>
  );

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await dataProvider.sincronizarAgenda();
      notify(
        r.google === false ? t("google_sin_conectar") : t("sincronizado"),
        { type: r.error ? "warning" : "success" },
      );
      await refetch();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setSincronizando(false);
    }
  };

  const sync = estado?.sync;
  return (
    <section
      className="rounded-lg border bg-card px-4 py-3 space-y-2"
      data-testid="agenda-ajustes"
    >
      <h2 className="etiqueta">{t("titulo")}</h2>
      <p className="text-xs text-muted-foreground">{t("ayuda")}</p>
      <div className="flex items-center justify-between gap-4 py-2 border-b">
        <Label htmlFor="agenda-propia">{t("propia")}</Label>
        <Switch
          id="agenda-propia"
          checked={valor.propia}
          onCheckedChange={(v) => cambiar({ ...valor, propia: v })}
        />
      </div>
      {numero("duracionMin", "unidad_minutos")}
      {numero("bloqueMin", "unidad_minutos")}
      {numero("pasoMin", "unidad_minutos")}
      {numero("antelacionHoras", "unidad_horas")}
      {numero("dias", "unidad_dias")}
      {numero("recordatorioHoras", "unidad_horas")}
      {/* 42: a quién da primero las consultorías el reparto (por temporadas). */}
      <div className="flex items-center justify-between gap-4 py-2 border-b">
        <Label htmlFor="agenda-prioridad">{t("prioridad")}</Label>
        <select
          id="agenda-prioridad"
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={valor.prioridad}
          onChange={(e) =>
            cambiar({
              ...valor,
              prioridad:
                e.target.value === "consultores" ? "consultores" : "admins",
            })
          }
        >
          <option value="admins">{t("prioridad_admins")}</option>
          <option value="consultores">{t("prioridad_consultores")}</option>
        </select>
      </div>

      <div className="pt-2 space-y-1">
        <h3 className="text-sm font-medium">{t("quien")}</h3>
        <ul className="text-sm space-y-0.5" data-testid="agenda-personas">
          {(estado?.personas ?? []).map((p) => (
            <li key={String(p.sale_id)}>
              {nombre(p.sale_id) || "…"}:{" "}
              <span
                className={
                  p.franjas ? "" : "text-amber-800 dark:text-amber-300"
                }
              >
                {p.franjas ? t("franjas", { n: p.franjas }) : t("sin_horario")}
              </span>
              {" · "}
              <span className="text-muted-foreground">
                {p.calendario ?? t("calendario_por_defecto")}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {t("horario_en_perfil")}
        </p>
      </div>

      <div className="pt-2 space-y-1">
        <h3 className="text-sm font-medium">{t("google")}</h3>
        <p className="text-sm" data-testid="agenda-sync">
          {sync?.ultimo_at
            ? `${formatoFechaHora(sync.ultimo_at)} · ${sync.ok ? t("bien") : t("revisar")}${
                sync.mensaje ? ` · ${sync.mensaje}` : ""
              }`
            : t("google_nunca")}
        </p>
        {estado?.citas_sin_calendario ? (
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {t("citas_sin_calendario", { n: estado.citas_sin_calendario })}
          </p>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={sincronizando}
          onClick={sincronizar}
        >
          {t("sincronizar")}
        </Button>
      </div>
    </section>
  );
};
